import { useEffect, useState } from 'react'
import { Logo } from '#app/components/ui/logo.tsx'

// Shared alert → color map so the sidebar quick-list and the article priority
// bars stay visually linked (same mental model as the real app).
const ALERT_COLORS: Record<string, string> = {
	'Financement OSBL': '#5D2FF0',
	'PME Saguenay': '#1F9D68',
	'Projet de loi 42': '#D88A1D',
	'Infrastructures Québec': '#2563EB',
}

interface ArticleItem {
	media: string
	connector: string
	published: string
	captured: string
	title: string
	alerts: string[]
	terms: string[]
}

const ARTICLES: ArticleItem[] = [
	{
		media: 'Le Devoir',
		connector: 'Bibliothèque Vigilio',
		published: 'Il y a 12 min',
		captured: 'il y a 4 min',
		title:
			'Québec annonce une réforme majeure du financement des OSBL culturels',
		alerts: ['Financement OSBL'],
		terms: ['financement', 'OSBL culturels', 'réforme'],
	},
	{
		media: 'La Presse',
		connector: 'Google Actualités',
		published: 'Il y a 34 min',
		captured: 'il y a 8 min',
		title:
			'Les PME manufacturières du Saguenay réclament une table de concertation',
		alerts: ['PME Saguenay'],
		terms: ['PME', 'Saguenay', 'manufacturières'],
	},
	{
		media: 'Radio-Canada',
		connector: 'Bibliothèque Vigilio',
		published: 'Il y a 1 h',
		captured: 'il y a 12 min',
		title:
			'Projet de loi 42 : les associations professionnelles appelées en commission',
		alerts: ['Projet de loi 42'],
		terms: ['projet de loi 42', 'associations professionnelles'],
	},
	{
		media: 'Journal de Québec',
		connector: 'Google Actualités',
		published: 'Il y a 2 h',
		captured: 'il y a 22 min',
		title: 'Infrastructures : la ville débloque 18 M$ pour le centre-ville',
		alerts: ['Infrastructures Québec'],
		terms: ['infrastructures', 'centre-ville'],
	},
]

export function DashboardMockup({ compact = false }: { compact?: boolean }) {
	return (
		<div
			className="relative flex w-full flex-col overflow-hidden"
			style={{
				aspectRatio: '16 / 10',
				borderRadius: 16,
				background: '#0E1220',
				boxShadow: 'var(--shadow-dark)',
				border: '1px solid rgba(255,255,255,0.08)',
			}}
		>
			<WindowChrome />
			<div className="flex min-h-0 flex-1">
				<Sidebar />
				<MainPane compact={compact} />
			</div>
		</div>
	)
}

function WindowChrome() {
	return (
		<div
			className="flex shrink-0 items-center gap-2 px-3.5"
			style={{
				height: 34,
				background: 'rgba(255,255,255,0.02)',
				borderBottom: '1px solid rgba(255,255,255,0.06)',
			}}
		>
			<div className="flex gap-1.5">
				{['#FF6159', '#FFBD2E', '#28C93F'].map((c) => (
					<span
						key={c}
						className="size-2.5 rounded-full opacity-85"
						style={{ background: c }}
					/>
				))}
			</div>
			<div
				className="ml-5 font-mono text-[11px]"
				style={{ color: 'rgba(255,255,255,0.4)' }}
			>
				vigilio.ca / tableau-de-bord
			</div>
		</div>
	)
}

const QUICK_ALERTS: { name: string; count: number }[] = [
	{ name: 'Financement OSBL', count: 42 },
	{ name: 'PME Saguenay', count: 18 },
	{ name: 'Projet de loi 42', count: 12 },
	{ name: 'Infrastructures Québec', count: 7 },
]

const TOP_ALERTS_MOCK: {
	name: string
	frequency: string
	lastMatch: string
	count: number
	trend: number
}[] = [
	{
		name: 'Financement OSBL',
		frequency: 'Quotidien',
		lastMatch: 'il y a 4 min',
		count: 42,
		trend: 8,
	},
	{
		name: 'PME Saguenay',
		frequency: 'Quotidien',
		lastMatch: 'il y a 8 min',
		count: 18,
		trend: 3,
	},
]

const EMERGING_TERMS_MOCK: { term: string; count: number; hot?: boolean }[] = [
	{ term: 'financement', count: 12, hot: true },
	{ term: 'OSBL culturels', count: 9 },
	{ term: 'PME Saguenay', count: 7, hot: true },
	{ term: 'projet de loi 42', count: 6 },
	{ term: 'associations', count: 5 },
	{ term: 'infrastructures', count: 4 },
	{ term: 'centre-ville', count: 3 },
]

function Sidebar() {
	return (
		<aside
			className="flex shrink-0 flex-col gap-0.5 px-3 py-4"
			style={{
				width: 200,
				background: '#0E1220',
				borderRight: '1px solid rgba(0,0,0,0.2)',
			}}
		>
			<div className="flex items-center gap-2 px-2 pb-3 pt-1">
				<Logo size="sm" color="#F6F5F1" asLink={false} />
			</div>

			<SideItem active label="Tableau de bord" icon="dashboard" />
			<SideItem label="Sources" icon="sources" />
			<SideItem label="Alertes" icon="bell" />
			<SideItem label="Articles" icon="articles" />
			<SideItem label="Diffusion" icon="mail" />

			<div
				className="mt-5 pt-3"
				style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
			>
				<div
					className="mb-1.5 px-2 uppercase"
					style={{
						fontFamily: 'var(--font-claire-mono)',
						fontSize: 9,
						letterSpacing: '0.14em',
						color: 'rgba(255,255,255,0.45)',
						fontWeight: 500,
					}}
				>
					Mes alertes
				</div>
				<div className="flex flex-col">
					{QUICK_ALERTS.map((a) => (
						<div
							key={a.name}
							className="flex items-center gap-2 px-2 py-1 text-[11px]"
							style={{ color: 'rgba(255,255,255,0.62)' }}
						>
							<span
								aria-hidden="true"
								className="shrink-0 rounded-full"
								style={{
									width: 5,
									height: 5,
									background: ALERT_COLORS[a.name] ?? '#9AA0AE',
								}}
							/>
							<span className="min-w-0 flex-1 truncate">{a.name}</span>
							<span
								className="shrink-0"
								style={{
									fontFamily: 'var(--font-claire-mono)',
									fontSize: 9.5,
									color: 'rgba(255,255,255,0.45)',
								}}
							>
								{a.count}
							</span>
						</div>
					))}
				</div>
			</div>

			<div
				className="mt-auto flex items-center gap-2 border-t pt-3"
				style={{ borderColor: 'rgba(255,255,255,0.08)' }}
			>
				<div
					className="flex size-[26px] items-center justify-center rounded-full text-[10px] font-medium text-white"
					style={{
						background: 'linear-gradient(135deg, #7B5CFF, #3F1CB8)',
						fontFamily: 'var(--font-claire-display)',
					}}
				>
					OR
				</div>
				<div className="min-w-0 flex-1">
					<div
						className="truncate text-[11px] font-medium"
						style={{ color: 'white' }}
					>
						Ordre professionnel
					</div>
					<div
						className="text-[9.5px] uppercase"
						style={{
							color: 'rgba(255,255,255,0.45)',
							letterSpacing: '0.06em',
							fontFamily: 'var(--font-claire-mono)',
						}}
					>
						Plan Business
					</div>
				</div>
			</div>
		</aside>
	)
}

function MainPane({ compact }: { compact: boolean }) {
	return (
		<div
			className="relative flex min-w-0 flex-1 flex-col overflow-hidden"
			style={{
				background: '#F6F5F1',
				color: '#0E1220',
			}}
		>
			<PageHeader />
			<div
				className="flex min-h-0 flex-1 flex-col gap-5 overflow-hidden px-7 pt-5 pb-4"
				style={{ color: '#0E1220' }}
			>
				<StatStrip />
				<TwoColumn compact={compact} />
			</div>
		</div>
	)
}

function PageHeader() {
	// Swap the placeholder date for the real day on the client only. Rendering
	// `new Date()` during SSR would desync with the client's timezone and
	// trigger a hydration warning, so we keep a static fallback for first paint.
	const [today, setToday] = useState('Vendredi 17 avril')
	useEffect(() => {
		try {
			setToday(
				new Intl.DateTimeFormat('fr-CA', {
					weekday: 'long',
					day: 'numeric',
					month: 'long',
				}).format(new Date()),
			)
		} catch {
			/* keep fallback */
		}
	}, [])

	return (
		<div
			className="flex items-end justify-between gap-4 px-7 pt-5 pb-4"
			style={{ borderBottom: '1px solid #E6E3DA' }}
		>
			<div>
				<div
					className="mb-2 inline-flex items-center gap-2 uppercase"
					style={{
						fontFamily: 'var(--font-claire-mono)',
						fontSize: 10,
						letterSpacing: '0.18em',
						color: '#5D2FF0',
					}}
				>
					<span
						aria-hidden="true"
						style={{
							width: 20,
							height: 1,
							background: 'currentColor',
							opacity: 0.6,
						}}
					/>
					{today}
				</div>
				<div
					style={{
						fontFamily: 'var(--font-claire-display)',
						fontWeight: 400,
						fontSize: 28,
						lineHeight: 1,
						letterSpacing: '-0.035em',
						color: '#0E1220',
					}}
				>
					Résumé{' '}
					<span style={{ fontStyle: 'italic', fontWeight: 300, color: '#6B7284' }}>
						du matin
					</span>
				</div>
			</div>
			<div className="flex shrink-0 items-center gap-2">
				<div
					className="inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[11px]"
					style={{
						background: 'transparent',
						border: '1px solid #E6E3DA',
						color: '#0E1220',
					}}
				>
					<svg width="10" height="10" viewBox="0 0 16 16" fill="none">
						<path
							d="M8 2v4M8 10v4M2 8h4M10 8h4M4 4l1.5 1.5M10.5 10.5 12 12M12 4l-1.5 1.5M5.5 10.5 4 12"
							stroke="currentColor"
							strokeWidth="1.4"
							strokeLinecap="round"
						/>
					</svg>
					Synthèse IA
				</div>
				<div
					className="inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[11px] font-medium text-white"
					style={{
						background: '#5D2FF0',
						boxShadow: '0 8px 20px -10px rgba(93,47,240,0.35)',
					}}
				>
					+ Nouvelle alerte
				</div>
			</div>
		</div>
	)
}

function StatStrip() {
	const stats: {
		label: string
		value: string
		delta?: string
		hint?: string
	}[] = [
		{
			label: 'Articles captés',
			value: '395',
			delta: '+18',
			hint: 'Depuis dernier envoi',
		},
		{ label: 'Alertes actives', value: '6' },
		{ label: 'Sources', value: '2' },
		{
			label: 'Correspondances',
			value: '452',
			delta: '+7',
			hint: 'Depuis dernier envoi',
		},
	]
	return (
		<div>
			<div
				className="mb-3 flex items-baseline gap-3"
				style={{ color: '#0E1220' }}
			>
				<div
					style={{
						fontFamily: 'var(--font-claire-display)',
						fontSize: 15,
						fontWeight: 400,
						letterSpacing: '-0.02em',
					}}
				>
					État de la veille
				</div>
				<div
					className="uppercase"
					style={{
						fontFamily: 'var(--font-claire-mono)',
						fontSize: 9.5,
						color: '#6B7284',
						letterSpacing: '0.08em',
					}}
				>
					Dernier envoi · il y a 2 h
				</div>
			</div>
			<div
				className="grid grid-cols-4"
				style={{
					borderTop: '1px solid #E6E3DA',
					borderBottom: '1px solid #E6E3DA',
				}}
			>
				{stats.map((s, i) => (
					<div
						key={s.label}
						className="px-4 pt-3 pb-3"
						style={{
							borderRight:
								i < stats.length - 1 ? '1px solid #E6E3DA' : 'none',
						}}
					>
						<div
							className="mb-1.5 uppercase"
							style={{
								fontFamily: 'var(--font-claire-mono)',
								fontSize: 9.5,
								color: '#6B7284',
								letterSpacing: '0.14em',
								fontWeight: 500,
							}}
						>
							{s.label}
						</div>
						<div className="flex items-baseline gap-1.5">
							<div
								style={{
									fontFamily: 'var(--font-claire-display)',
									fontWeight: 400,
									fontSize: 26,
									lineHeight: 1,
									letterSpacing: '-0.035em',
									color: '#0E1220',
								}}
							>
								{s.value}
							</div>
							{s.delta ? (
								<div
									style={{
										fontFamily: 'var(--font-claire-mono)',
										fontSize: 10,
										color: '#1F9D68',
										letterSpacing: '0.05em',
									}}
								>
									{s.delta}
								</div>
							) : null}
						</div>
						{s.hint ? (
							<div
								className="mt-1 uppercase"
								style={{
									fontFamily: 'var(--font-claire-mono)',
									fontSize: 8.5,
									color: '#9AA0AE',
									letterSpacing: '0.08em',
								}}
							>
								{s.hint}
							</div>
						) : null}
					</div>
				))}
			</div>
		</div>
	)
}

function TwoColumn({ compact }: { compact: boolean }) {
	return (
		<div className="grid min-h-0 flex-1 gap-6 overflow-hidden lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
			<ArticlesPane compact={compact} />
			<SidePanels />
		</div>
	)
}

function SidePanels() {
	return (
		<div className="flex flex-col gap-4 overflow-hidden">
			<TopAlertsPane />
			<EmergingTermsPane />
			<SourcesPane />
		</div>
	)
}

function SidePanelHeading({ label }: { label: string }) {
	return (
		<div
			className="mb-1.5"
			style={{
				fontFamily: 'var(--font-claire-display)',
				fontSize: 13,
				fontWeight: 400,
				letterSpacing: '-0.02em',
				color: '#0E1220',
			}}
		>
			{label}
		</div>
	)
}

function TopAlertsPane() {
	return (
		<div>
			<SidePanelHeading label="Alertes les plus actives" />
			<div className="flex flex-col">
				{TOP_ALERTS_MOCK.map((a) => (
					<div
						key={a.name}
						className="flex items-center gap-2.5 py-1.5"
						style={{ borderBottom: '1px solid #EFEDE5' }}
					>
						<span
							aria-hidden="true"
							style={{
								width: 2.5,
								height: 22,
								borderRadius: 2,
								background: ALERT_COLORS[a.name] ?? '#9AA0AE',
								flex: 'none',
							}}
						/>
						<div className="min-w-0 flex-1">
							<div
								className="truncate"
								style={{
									fontFamily: 'var(--font-claire-display)',
									fontSize: 12,
									color: '#0E1220',
									letterSpacing: '-0.01em',
									lineHeight: 1.2,
								}}
							>
								{a.name}
							</div>
							<div
								className="uppercase"
								style={{
									fontFamily: 'var(--font-claire-mono)',
									fontSize: 8.5,
									color: '#6B7284',
									letterSpacing: '0.06em',
								}}
							>
								{a.frequency} · {a.lastMatch}
							</div>
						</div>
						<div className="shrink-0 text-right">
							<div
								style={{
									fontFamily: 'var(--font-claire-display)',
									fontSize: 14,
									color: '#0E1220',
									letterSpacing: '-0.02em',
									lineHeight: 1,
								}}
							>
								{a.count}
							</div>
							<div
								style={{
									fontFamily: 'var(--font-claire-mono)',
									fontSize: 8.5,
									color: '#1F9D68',
								}}
							>
								+{a.trend}
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

function EmergingTermsPane() {
	return (
		<div>
			<SidePanelHeading label="Termes en émergence" />
			<div className="flex flex-wrap gap-1">
				{EMERGING_TERMS_MOCK.map((t) => (
					<span
						key={t.term}
						className="inline-flex items-center gap-1 rounded-[3px]"
						style={{
							padding: '2px 6px',
							fontFamily: 'var(--font-claire-mono)',
							fontSize: 9,
							letterSpacing: '0.04em',
							fontWeight: 500,
							...(t.hot
								? {
									background: 'rgba(93,47,240,0.08)',
									color: '#3F1CB8',
								}
								: {
									background: 'transparent',
									border: '1px solid #E6E3DA',
									color: '#2A2F42',
								}),
						}}
					>
						{t.hot ? (
							<svg
								width="8"
								height="8"
								viewBox="0 0 16 16"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.4"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<path d="M8 14c2.5 0 4.5-1.8 4.5-4.2 0-2-1.5-3-2-4.8-.3-1.3 0-2.5-.5-3-1 1-3 3-3 5 0 .8.3 1.3.3 1.3S6 7 5 7.5c-1 .5-1.5 1.5-1.5 2.8C3.5 12.3 5.5 14 8 14Z" />
							</svg>
						) : null}
						{t.term}
						<span style={{ opacity: 0.55, marginLeft: 1 }}>·{t.count}</span>
					</span>
				))}
			</div>
		</div>
	)
}

function ArticlesPane({ compact }: { compact: boolean }) {
	return (
		<div className="min-h-0 overflow-hidden">
			<div className="mb-2 flex items-baseline gap-3">
				<div
					style={{
						fontFamily: 'var(--font-claire-display)',
						fontSize: 15,
						fontWeight: 400,
						letterSpacing: '-0.02em',
						color: '#0E1220',
					}}
				>
					En attente d'envoi
				</div>
				<div
					className="uppercase"
					style={{
						fontFamily: 'var(--font-claire-mono)',
						fontSize: 9.5,
						color: '#6B7284',
						letterSpacing: '0.08em',
					}}
				>
					7 à diffuser
				</div>
				<span className="ml-auto" />
				<div
					className="italic"
					style={{
						fontFamily: 'var(--font-claire-display)',
						fontSize: 12,
						fontWeight: 300,
						color: '#6B7284',
					}}
				>
					Voir tout →
				</div>
			</div>
			<div className="flex flex-col">
				{ARTICLES.slice(0, compact ? 2 : 3).map((a, i) => (
					<ArticleRow key={i} {...a} />
				))}
			</div>
		</div>
	)
}

function ArticleRow({
	media,
	published,
	captured,
	title,
	alerts,
	terms,
}: ArticleItem) {
	const pc = (alerts[0] && ALERT_COLORS[alerts[0]]) ?? '#9AA0AE'
	return (
		<div
			className="relative grid gap-4"
			style={{
				gridTemplateColumns: 'minmax(0, 1fr) 110px',
				alignItems: 'flex-start',
				padding: '12px 10px 12px 14px',
				borderBottom: '1px solid #EFEDE5',
			}}
		>
			<span
				aria-hidden="true"
				style={{
					position: 'absolute',
					left: 0,
					top: 12,
					bottom: 12,
					width: 2,
					background: pc,
					borderRadius: 2,
					opacity: 0.9,
				}}
			/>
			<div className="min-w-0">
				<div className="mb-1 flex flex-wrap items-baseline gap-2">
					<span
						style={{
							fontFamily: 'var(--font-claire-body)',
							fontSize: 10.5,
							fontWeight: 500,
							letterSpacing: '-0.005em',
							color: '#6B7284',
						}}
					>
						{media}
					</span>
					{alerts[0] ? (
						<>
							<span
								style={{
									fontFamily: 'var(--font-claire-mono)',
									fontSize: 9,
									color: '#6B7284',
									opacity: 0.6,
								}}
							>
								·
							</span>
							<span
								className="uppercase"
								style={{
									fontFamily: 'var(--font-claire-mono)',
									fontSize: 9,
									color: '#3F1CB8',
									letterSpacing: '0.08em',
								}}
							>
								{alerts[0]}
							</span>
						</>
					) : null}
				</div>
				<div
					style={{
						fontFamily: 'var(--font-claire-body)',
						fontSize: 12,
						fontWeight: 500,
						lineHeight: 1.35,
						letterSpacing: '-0.005em',
						color: '#0E1220',
						display: '-webkit-box',
						WebkitLineClamp: 2,
						WebkitBoxOrient: 'vertical',
						overflow: 'hidden',
					}}
				>
					{title}
				</div>
				{alerts.length > 1 || terms.length > 0 ? (
					<div className="mt-1.5 flex flex-wrap gap-1">
						{alerts.slice(1, 3).map((a) => (
							<span
								key={`a-${a}`}
								className="inline-flex items-center gap-1 rounded-[3px]"
								style={{
									padding: '1.5px 6px',
									background: 'rgba(93,47,240,0.08)',
									color: '#3F1CB8',
									fontFamily: 'var(--font-claire-mono)',
									fontSize: 9,
									letterSpacing: '0.04em',
									fontWeight: 500,
								}}
							>
								<span
									aria-hidden="true"
									style={{
										width: 4,
										height: 4,
										borderRadius: 4,
										background: 'currentColor',
									}}
								/>
								{a}
							</span>
						))}
						{terms.slice(0, 4).map((t) => (
							<span
								key={`t-${t}`}
								className="inline-flex items-center rounded-[3px]"
								style={{
									padding: '1.5px 6px',
									background: 'transparent',
									border: '1px solid #E6E3DA',
									color: '#2A2F42',
									fontFamily: 'var(--font-claire-mono)',
									fontSize: 9,
									letterSpacing: '0.04em',
									fontWeight: 500,
								}}
							>
								{t}
							</span>
						))}
					</div>
				) : null}
			</div>
			<div
				className="text-right"
				style={{
					fontFamily: 'var(--font-claire-mono)',
					fontSize: 9,
					color: '#9AA0AE',
					letterSpacing: '-0.003em',
					paddingTop: 2,
				}}
			>
				<div
					style={{
						color: '#2A2F42',
						fontWeight: 500,
						marginBottom: 2,
					}}
				>
					{published}
				</div>
				<div>Capté {captured}</div>
			</div>
		</div>
	)
}

function SourcesPane() {
	return (
		<div>
			<SidePanelHeading label="Sources connectées" />
			<div
				style={{
					padding: '10px 12px',
					background: '#FFFDF8',
					border: '1px solid #E6E3DA',
					borderRadius: 4,
				}}
			>
				<div className="mb-2 flex items-center gap-2">
					<span
						aria-hidden="true"
						style={{
							width: 5,
							height: 5,
							borderRadius: 5,
							background: '#1F9D68',
							boxShadow: '0 0 8px rgba(31,157,104,0.5)',
						}}
					/>
					<span
						className="uppercase"
						style={{
							fontFamily: 'var(--font-claire-mono)',
							fontSize: 9.5,
							color: '#1F9D68',
							letterSpacing: '0.14em',
							fontWeight: 500,
						}}
					>
						2 sources actives
					</span>
				</div>
				<div
					className="italic"
					style={{
						fontFamily: 'var(--font-claire-display)',
						fontSize: 12,
						fontWeight: 300,
						color: '#6B7284',
					}}
				>
					Dernière collecte il y a 12 min.
				</div>
				<div
					className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium"
					style={{ color: '#5D2FF0' }}
				>
					Gérer les sources →
				</div>
			</div>
		</div>
	)
}

function SideItem({
	label,
	icon,
	active,
}: {
	label: string
	icon: 'dashboard' | 'bell' | 'sources' | 'articles' | 'mail'
	active?: boolean
}) {
	return (
		<div
			className="relative flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[12px]"
			style={{
				background: active ? 'rgba(255,255,255,0.06)' : 'transparent',
				color: active ? 'white' : 'rgba(255,255,255,0.7)',
				fontWeight: active ? 500 : 400,
			}}
		>
			{active ? (
				<span
					className="absolute top-1.5 bottom-1.5 w-0.5 rounded"
					style={{ left: -12, background: '#7B5CFF' }}
				/>
			) : null}
			<SideIcon type={icon} active={active} />
			<span>{label}</span>
		</div>
	)
}

function SideIcon({
	type,
	active,
}: {
	type: 'dashboard' | 'bell' | 'sources' | 'articles' | 'mail'
	active?: boolean
}) {
	const c = active ? '#a89dff' : 'rgba(255,255,255,0.55)'
	const s = { width: 13, height: 13, flex: 'none' as const }
	const sw = 1.4
	const common = {
		fill: 'none' as const,
		stroke: c,
		strokeWidth: sw,
		strokeLinecap: 'round' as const,
		strokeLinejoin: 'round' as const,
	}
	switch (type) {
		case 'dashboard':
			return (
				<svg style={s} viewBox="0 0 16 16" {...common}>
					<path d="M2 9.2 8 3l6 6.2V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V9.2Z" />
					<path d="M6.5 14V9.5h3V14" />
				</svg>
			)
		case 'bell':
			return (
				<svg style={s} viewBox="0 0 16 16" {...common}>
					<path d="M3.5 7a4.5 4.5 0 0 1 9 0v2.5l1 2h-11l1-2V7Z" />
					<path d="M6.5 13a1.5 1.5 0 0 0 3 0" />
				</svg>
			)
		case 'sources':
			return (
				<svg style={s} viewBox="0 0 16 16" {...common}>
					<path d="M2.5 4.5 8 2l5.5 2.5L8 7 2.5 4.5Z" />
					<path d="M2.5 8 8 10.5 13.5 8" />
					<path d="M2.5 11.5 8 14l5.5-2.5" />
				</svg>
			)
		case 'articles':
			return (
				<svg style={s} viewBox="0 0 16 16" {...common}>
					<path d="M3 2h7l3 3v9H3V2Z" />
					<path d="M10 2v3h3M5.5 8h5M5.5 10.5h5M5.5 5.5h2" />
				</svg>
			)
		case 'mail':
			return (
				<svg style={s} viewBox="0 0 16 16" {...common}>
					<path d="M2 4h12v8H2z" />
					<path d="m2.5 4.5 5.5 4 5.5-4" />
				</svg>
			)
	}
}
